// GoldSrc WAD3 archive + MIPTEX decoding.
// Framework-agnostic: returns raw RGBA bytes, so it runs in Node and the browser.

export interface DecodedTexture {
  name: string
  width: number
  height: number
  /** Tightly packed RGBA8, length = width * height * 4. */
  rgba: Uint8Array
  /** True for `{`-prefixed textures that use palette index 255 as transparent. */
  transparent: boolean
}

const TRANSPARENT_INDEX = 255

function readName(dv: DataView, base: number, max: number): string {
  let name = ''
  for (let i = 0; i < max; i++) {
    const ch = dv.getUint8(base + i)
    if (ch === 0) break
    name += String.fromCharCode(ch)
  }
  return name
}

/**
 * Decode a MIPTEX structure. Works for textures embedded in a BSP and for lumps
 * stored inside a WAD3 — the on-disk layout is identical. `base` is the byte
 * offset of the miptex header within `dv`.
 *
 * Returns null when the miptex carries no pixel data (mip offset 0 → the real
 * texture lives in an external WAD, referenced by name).
 */
export function decodeMipTex(
  dv: DataView,
  base: number,
): DecodedTexture | null {
  const name = readName(dv, base, 16)
  const width = dv.getUint32(base + 16, true)
  const height = dv.getUint32(base + 20, true)
  const mip0 = dv.getUint32(base + 24, true)
  const mip3 = dv.getUint32(base + 36, true)
  if (mip0 === 0 || width === 0 || height === 0) return null

  // Palette (256 * RGB) follows the 4th mip level.
  const paletteCountOffset = base + mip3 + (width >> 3) * (height >> 3)
  const paletteBase = paletteCountOffset + 2 // skip the uint16 palette size (256)

  const transparent = name.startsWith('{')
  const pixels = width * height
  const rgba = new Uint8Array(pixels * 4)
  const pixelBase = base + mip0

  for (let p = 0; p < pixels; p++) {
    const idx = dv.getUint8(pixelBase + p)
    const o = p * 4
    if (transparent && idx === TRANSPARENT_INDEX) {
      // leave RGBA at 0 → fully transparent
      continue
    }
    const pal = paletteBase + idx * 3
    rgba[o] = dv.getUint8(pal)
    rgba[o + 1] = dv.getUint8(pal + 1)
    rgba[o + 2] = dv.getUint8(pal + 2)
    rgba[o + 3] = 255
  }

  return { name, width, height, rgba, transparent }
}

/**
 * Decode a `{`-prefixed gradient decal (e.g. the bullet hole `{shot1`). Unlike a
 * normal `{` texture (index 255 = transparent), GoldSrc decals store the mark as
 * a dark-on-bright gradient and the engine renders them with
 * `alpha = 255 - luminance`, tinted by palette entry 255 (black for bullet holes,
 * dark red for blood). We bake that into straight RGBA so a plain transparent
 * material shows the mark on a see-through background.
 */
export function decodeDecalMipTex(
  dv: DataView,
  base: number,
): DecodedTexture | null {
  const name = readName(dv, base, 16)
  const width = dv.getUint32(base + 16, true)
  const height = dv.getUint32(base + 20, true)
  const mip0 = dv.getUint32(base + 24, true)
  const mip3 = dv.getUint32(base + 36, true)
  if (mip0 === 0 || width === 0 || height === 0) return null

  const paletteBase = base + mip3 + (width >> 3) * (height >> 3) + 2
  const tint = paletteBase + TRANSPARENT_INDEX * 3
  const tr = dv.getUint8(tint)
  const tg = dv.getUint8(tint + 1)
  const tb = dv.getUint8(tint + 2)

  const pixels = width * height
  const rgba = new Uint8Array(pixels * 4)
  for (let p = 0; p < pixels; p++) {
    const idx = dv.getUint8(base + mip0 + p)
    const pal = paletteBase + idx * 3
    const lum =
      (dv.getUint8(pal) + dv.getUint8(pal + 1) + dv.getUint8(pal + 2)) / 3
    const o = p * 4
    rgba[o] = tr
    rgba[o + 1] = tg
    rgba[o + 2] = tb
    // dark mark → opaque, bright background → clear. Boost contrast (×1.6) so the
    // hole reads as a solid mark instead of a faint smudge.
    rgba[o + 3] = Math.min(255, (255 - lum) * 1.6)
  }
  return { name, width, height, rgba, transparent: true }
}

interface WadLump {
  filepos: number
  type: number
}

/** A parsed WAD3/WAD2 texture archive. Look textures up by name (case-insensitive). */
export class Wad {
  private readonly dv: DataView
  private readonly lumps = new Map<string, WadLump>()

  constructor(buffer: ArrayBuffer) {
    this.dv = new DataView(buffer)

    const magic = readName(this.dv, 0, 4)
    if (magic !== 'WAD3' && magic !== 'WAD2') {
      throw new Error(`Not a WAD archive (magic="${magic}")`)
    }

    const numLumps = this.dv.getInt32(4, true)
    const dirOffset = this.dv.getInt32(8, true)

    for (let i = 0; i < numLumps; i++) {
      const entry = dirOffset + i * 32
      const filepos = this.dv.getInt32(entry, true)
      const type = this.dv.getUint8(entry + 12)
      const name = readName(this.dv, entry + 16, 16)
      this.lumps.set(name.toLowerCase(), { filepos, type })
    }
  }

  get size(): number {
    return this.lumps.size
  }

  /** Returns the decoded texture for `name`, or null if absent. */
  getTexture(name: string): DecodedTexture | null {
    const lump = this.lumps.get(name.toLowerCase())
    if (!lump) return null
    return decodeMipTex(this.dv, lump.filepos)
  }

  /** Decode a gradient decal (bullet hole / blood) with baked alpha. */
  getDecal(name: string): DecodedTexture | null {
    const lump = this.lumps.get(name.toLowerCase())
    if (!lump) return null
    return decodeDecalMipTex(this.dv, lump.filepos)
  }
}
